import { forwardRef, useId, useState } from "react";

import { Eye, EyeOff } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * The hosted Universal Login password field, measured live 2026-10-13:
 *
 *   52px tall, 8px radius (theme input radius), 1px theme input border,
 *   theme input background, 16px text in the "filled text" colour.
 *   Label sits inside at rest (theme label colour) and floats onto the top
 *   border once the field is focused or filled. It turns the focus colour
 *   while focused and the ERROR colour, along with the border, while invalid.
 *   A 44px eye toggle sits at the right edge.
 *
 * Built here rather than reusing ULThemePasswordField because that one is
 * 56px with its own ring and shadow treatment, and restyling it would change
 * the signup screens that use it. See UniversalLoginWidget.tsx.
 */
export interface PasswordInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
  label: string;
  invalid?: boolean;
  showLabel?: string;
  hideLabel?: string;
}

const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      label,
      invalid = false,
      showLabel = "Show password",
      hideLabel = "Hide password",
      className,
      onFocus,
      onBlur,
      ...props
    },
    ref
  ) => {
    const id = useId();
    const [visible, setVisible] = useState(false);
    const [focused, setFocused] = useState(false);
    const filled = typeof props.value === "string" && props.value.length > 0;
    const floating = focused || filled;

    const borderColor = invalid
      ? "var(--ul-theme-color-error, #d03c38)"
      : focused
        ? "var(--ul-theme-color-base-focus-color, #294fed)"
        : "var(--ul-theme-color-input-border, #d1d5db)";
    const labelColor = invalid
      ? "var(--ul-theme-color-error, #d03c38)"
      : focused
        ? "var(--ul-theme-color-base-focus-color, #294fed)"
        : "var(--ul-theme-color-input-labels-placeholders, #6b7280)";

    return (
      <div
        className={cn(
          "relative flex h-[52px] w-full items-center rounded-[var(--ul-theme-border-input-border-radius,8px)]",
          className
        )}
        style={{
          backgroundColor: "var(--ul-theme-color-input-background, #f9fafb)",
          border: `var(--ul-theme-border-input-border-weight, 1px) solid ${borderColor}`,
          // Focus thickens the outline to ~2px, as on the hosted field.
          boxShadow: focused ? `0 0 0 1px ${borderColor}` : "none",
        }}
      >
        <label
          htmlFor={id}
          className="pointer-events-none absolute left-4 origin-left whitespace-nowrap px-1 -ml-1 transition-all duration-150 ease-out"
          style={{
            top: floating ? 0 : "50%",
            transform: floating
              ? "translateY(-50%) scale(0.875)"
              : "translateY(-50%)",
            fontSize: "var(--ul-theme-font-input-labels-size, 1rem)",
            color: labelColor,
            backgroundColor: floating
              ? "var(--ul-theme-color-input-background, #f9fafb)"
              : "transparent",
            lineHeight: 1,
          }}
        >
          {label}
        </label>
        <input
          {...props}
          ref={ref}
          id={id}
          type={visible ? "text" : "password"}
          aria-invalid={invalid || undefined}
          className="h-full min-w-0 flex-1 bg-transparent pl-4 pr-1 outline-none"
          style={{
            fontSize: "var(--ul-theme-font-input-labels-size, 1rem)",
            color: "var(--ul-theme-color-input-filled-text, #1d2b4f)",
          }}
          onFocus={(e) => {
            setFocused(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            onBlur?.(e);
          }}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? hideLabel : showLabel}
          aria-pressed={visible}
          className="flex h-full w-11 shrink-0 cursor-pointer items-center justify-center bg-transparent outline-none focus-visible:ring-1"
          style={{ color: "var(--ul-theme-color-icons, #6b7280)" }}
        >
          {visible ? (
            <EyeOff className="h-5 w-5" strokeWidth={1.5} />
          ) : (
            <Eye className="h-5 w-5" strokeWidth={1.5} />
          )}
        </button>
      </div>
    );
  }
);

PasswordInput.displayName = "PasswordInput";

export default PasswordInput;
